<template>
  <div class="tenders-phone-list">
    <BasePhoneList
      :items="tenders"
      :selecting="selecting"
      :sort-open="sortOpen"
      :sort-options="sortOptions"
      :sort-key="sortKey"
      :has-more="hasMore"
      :loading-more="loadingMore"
      :load-more-error="loadMoreError"
      :pending="pending"
      @update:sortOpen="$emit('update:sortOpen', $event)"
      @sort="$emit('sort', $event)"
      @load-more="$emit('load-more')"
    >
      <template #item="{ item: tender, selected, toggle }">
        <TenderListItem
          :key="tender.id"
          :tender="tender"
          :selectable="selecting"
          :selected="selected"
          :show-score="showScore"
          :archived-label="archivedBy(tender)"
          @open="$emit('open', tender.id)"
          @toggle-select="toggle(tender.id)"
          @follow="$emit('follow', tender)"
          @more="actionsTender = tender"
        />
      </template>
      <template #selection-actions="{ items, clear }">
        <TendersActionsManager :mode="mode" :tenders="items" @tender-status-updated="clear" />
      </template>
    </BasePhoneList>

    <BaseBottomSheet v-model="actionsSheetOpen" :title="sheetTitle" :can-go-back="assigning" @back="assigning = false">
      <!-- Assignees: picked here, saved when the sheet closes, as the desktop menu does. -->
      <BaseSheetChecklist
        v-if="actionsTender && assigning"
        v-model="draft"
        :choices="assigneeChoices"
        :label="sheetTitle"
        @done="actionsSheetOpen = false"
      />
      <template v-else-if="actionsTender">
        <!-- What the row leaves out, to look at without opening the tender (which reads it). -->
        <dl v-if="hasFacts" class="base-bottom-sheet__facts">
          <div v-if="publishedDate">
            <dt>{{ $t('page.tenders.data-table-titles.published') }}</dt>
            <dd>{{ publishedDate }}</dd>
          </div>
          <div v-if="closingDate">
            <dt>{{ $t('page.tenders.data-table-titles.closing') }}</dt>
            <dd>{{ closingDate }}</dd>
          </div>
          <div v-if="keywords.length">
            <dt>{{ $t('menu.keywords') }}</dt>
            <dd class="tenders-phone-list__keywords">
              <BaseTag v-for="keyword in keywords" :key="keyword" tone="info" preset="secondary">{{ keyword }}</BaseTag>
            </dd>
          </div>
          <div v-if="assigneeNames">
            <dt>{{ $t('page.tenders.data-table-titles.assignees') }}</dt>
            <dd>{{ assigneeNames }}</dd>
          </div>
        </dl>
        <ul class="base-bottom-sheet__menu tenders-phone-list__menu">
          <template v-if="showOpportunities">
            <li v-for="opportunity in linkedOpportunities" :key="opportunity.id">
              <button type="button" @click="act('open-opportunity', opportunity)">
                <BaseIcon name="target" />
                {{ linkedOpportunities.length === 1 ? $tc('actions.tender.view-opportunity', 1) : opportunity.name }}
              </button>
            </li>
            <li v-if="!linkedOpportunities.length">
              <button type="button" @click="act('convert')">
                <BaseIcon name="target" />
                {{ $tc('actions.tender.convert-to-opportunity', 1) }}
              </button>
            </li>
          </template>
          <li>
            <button type="button" @click="openAssignees">
              <BaseIcon name="users" />
              {{ $t('page.tenders.assignation.assign-to') }}
              <BaseIcon name="chevron-right" class="base-bottom-sheet__menu-end" />
            </button>
          </li>
          <li>
            <button type="button" :class="{ 'is-active': isFollowed }" @click="act('follow')">
              <BaseIcon name="bookmark" />
              {{ isFollowed ? $t('actions.unfollow') : $t('actions.follow') }}
            </button>
          </li>
          <li>
            <button type="button" :class="{ 'is-active': isShared }" @click="act('share')">
              <BaseIcon name="share-2" />
              {{ isShared ? $t('actions.unshare') : $t('actions.share') }}
            </button>
          </li>
          <li>
            <button type="button" @click="act('read')">
              <BaseIcon :name="isRead ? 'eye-off' : 'eye'" />
              {{ isRead ? $t('actions.unread') : $t('actions.read') }}
            </button>
          </li>
          <li>
            <button type="button" :class="{ 'is-active is-down': rating === 'DOWN' }" @click="act('rate-down')">
              <BaseIcon name="thumbs-down" />
              {{
                rating === 'DOWN' ? $t('actions.undislike') : $tc('tender-details.thumbs-rating.down-message.title', 1)
              }}
            </button>
          </li>
          <li>
            <button type="button" :class="{ 'is-active is-up': rating === 'UP' }" @click="act('rate-up')">
              <BaseIcon name="thumbs-up" />
              {{ rating === 'UP' ? $t('actions.unlike') : $tc('tender-details.thumbs-rating.up-message.title', 1) }}
            </button>
          </li>
          <li>
            <button type="button" :class="{ 'is-danger': !isArchived }" @click="act('archive')">
              <BaseIcon name="archive" />
              {{ isArchived ? $t('actions.tender.unarchive') : $t('actions.tender.archive') }}
            </button>
          </li>
        </ul>
      </template>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneList from '@/components/base/BasePhoneList.vue';
import BaseSheetChecklist from '@/components/base/BaseSheetChecklist.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import TenderListItem from '@/components/page-tenders/TenderListItem.vue';
import TendersActionsManager from '@/components/page-tenders/TendersActionsManager.vue';
import { areSameFlatArray } from '@/util/array-util';
import { formatShortDate, isUnknownDate, toMoment } from '@/util/date-util';
import { removeMarks } from '@/util/mark-util';
import { getSortingField, getSortingOrder } from '@/util/sorting-util';
import { getFullName } from '@/util/user-util';

// A date the tender doesn't have shows no line at all.
const dateOf = (date) => (isUnknownDate(date) ? '' : formatShortDate(toMoment(date)));

// The sortable columns of the desktop table, as one-tap choices.
const SORT_OPTIONS = [
  { key: 'published-desc', field: 'publishedSortable', desc: true },
  { key: 'published-asc', field: 'publishedSortable', desc: false },
  { key: 'closing-asc', field: 'closingSortable', desc: false },
  { key: 'closing-desc', field: 'closingSortable', desc: true },
  { key: 'title-asc', field: 'title', desc: false },
  { key: 'emitter-asc', field: 'emitter', desc: false },
];

// The phone version of the tenders table: compact rows, and in each row's sheet what the
// desktop table's columns show and change.
export default {
  name: 'TendersPhoneList',
  components: {
    BaseBottomSheet,
    BaseIcon,
    BasePhoneList,
    BaseSheetChecklist,
    BaseTag,
    TenderListItem,
    TendersActionsManager,
  },
  props: {
    tenders: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    selecting: {
      type: Boolean,
      default: false,
    },
    sortOpen: {
      type: Boolean,
      default: false,
    },
    showScore: {
      type: Boolean,
      default: false,
    },
    archivedBy: {
      type: Function,
      default: () => '',
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    pending: {
      type: Boolean,
      default: false,
    },
    sortingField: {
      type: String,
      default: 'publishedDate',
    },
    sortingOrder: {
      type: String,
      default: 'desc',
    },
    // Who the reader may assign.
    assignableUsers: {
      type: Array,
      default: () => [],
    },
    // The tenant has opportunities: a tender's sheet opens or creates its own.
    showOpportunities: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      actionsTender: null,
      assigning: false,
      // The assignees picked so far, by id.
      // The assignees ticked since the sheet opened, kept while going back to its actions.
      draft: null,
    };
  },
  computed: {
    sortOptions() {
      return SORT_OPTIONS.map((option) => ({
        ...option,
        label: this.$t(`page.tenders.phone-list.sort-options.${option.key}`),
      }));
    },
    sortKey() {
      const current = SORT_OPTIONS.find(
        (option) =>
          getSortingField(option.field) === this.sortingField && getSortingOrder(option.desc) === this.sortingOrder,
      );
      return current ? current.key : '';
    },
    actionsSheetOpen: {
      get() {
        return !!this.actionsTender;
      },
      set(open) {
        if (open) return;
        this.saveAssignees();
        this.actionsTender = null;
        this.assigning = false;
        this.draft = null;
      },
    },
    sheetTitle() {
      if (!this.actionsTender) return '';
      return this.assigning
        ? this.$t('page.tenders.data-table-titles.assignees')
        : removeMarks(this.actionsTender.title);
    },
    publishedDate() {
      return dateOf(this.actionsTender.published);
    },
    closingDate() {
      return dateOf(this.actionsTender.closingDate);
    },
    keywords() {
      return this.actionsTender.matchedKeywords || [];
    },
    assignees() {
      return this.actionsTender.assignees || [];
    },
    assigneeNames() {
      return this.assignees.map(getFullName).join(', ');
    },
    hasFacts() {
      return !!(this.publishedDate || this.closingDate || this.keywords.length || this.assigneeNames);
    },
    linkedOpportunities() {
      return this.actionsTender.opportunities || [];
    },
    assigneeChoices() {
      return this.assignableUsers
        .map((user) => ({ id: user.id, name: user.fullName, detail: user.email }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    isFollowed() {
      return !!this.actionsTender.userTender && !!this.actionsTender.userTender.followed;
    },
    isShared() {
      return !!this.actionsTender.tenantTender && !!this.actionsTender.tenantTender.shared;
    },
    rating() {
      return this.actionsTender.tenantTender ? this.actionsTender.tenantTender.thumbsRating : null;
    },
    isRead() {
      return !!this.actionsTender.userTender && !!this.actionsTender.userTender.read;
    },
    isArchived() {
      return !!this.actionsTender.tenantTender && !!this.actionsTender.tenantTender.archived;
    },
  },
  methods: {
    act(action, ...args) {
      const tender = this.actionsTender;
      this.actionsSheetOpen = false;
      this.$emit(action, tender, ...args);
    },
    openAssignees() {
      if (!this.draft) this.draft = this.assignees.map((assignee) => assignee.userId);
      this.assigning = true;
    },
    saveAssignees() {
      if (!this.draft) return;
      const before = this.assignees.map((assignee) => assignee.userId);
      if (!areSameFlatArray(before, [...this.draft])) this.$emit('assign', this.actionsTender, [...this.draft]);
    },
  },
};
</script>

<style lang="scss">
.tenders-phone-list {
  // The desktop toolbar floats over the table header; here it sits in the selection bar, on
  // as many lines as the screen needs.
  .status-manager {
    position: static;
    z-index: auto;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 4px;
    width: 100%;
    padding: 0;
    box-shadow: none;
  }

  &__keywords {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  // Over the sheet menu's own active color.
  &__menu button.is-active {
    &.is-down {
      color: $color-error;
    }

    &.is-up {
      color: $color-success;
    }
  }
}
</style>
